import React, {useEffect, useState} from "react";
import {Button, Card, Col, Layout, notification, Row, Select, Spin, Typography} from "antd";
import {billingServices} from "../../../../services/billing.services";
import {companyService} from "../../../../services/company.services";
import {useHistory} from "react-router-dom";

const {Title, Text, Paragraph} = Typography;

export default function ChooseTariffPage() {
    const [tariffs, setTariffs] = useState([]);
    const [legalEntities, setLegalEntities] = useState([]);
    const [selectedLegalEntityId, setSelectedLegalEntityId] = useState(null);
    const [loading, setLoading] = useState(true);
    const [subscribing, setSubscribing] = useState(null);
    const history = useHistory();

    useEffect(() => {
        Promise.all([
            billingServices.getTariffList(),
            companyService.getEntitiesList(),
        ])
            .then(([tariffResponse, entities]) => {
                if (tariffResponse.data && tariffResponse.data.status === 'success') {
                    setTariffs(tariffResponse.data.data);
                }

                // Оставляем только активные юр. лица (не в архиве)
                const activeLegalEntities = (Array.isArray(entities) ? entities : [])
                    .filter((entity) => !entity.Archived);

                setLegalEntities(activeLegalEntities);

                // По умолчанию выбираем первое активное юр. лицо
                if (activeLegalEntities.length > 0) {
                    setSelectedLegalEntityId(activeLegalEntities[0].Id);
                }
            })
            .catch((error) => {
                notification.error({
                    message: 'Ошибка',
                    description: error || 'Не удалось загрузить данные',
                });
            })
            .finally(() => setLoading(false));
    }, []);

    const handleSubscribe = async (tariffId) => {
        if (!selectedLegalEntityId) {
            notification.warning({
                message: 'Не выбрано юр. лицо',
                description: 'Для оформления подписки выберите юридическое лицо',
            });
            return;
        }

        setSubscribing(tariffId);
        try {
            const response = await billingServices.subscribe(tariffId, selectedLegalEntityId);
            if (response.data && response.data.status === 'success') {
                notification.success({
                    message: 'Подписка оформлена',
                    description: response.data.message || 'Счет на оплату сформирован',
                });
                history.push('/start');
            } else {
                notification.error({
                    message: 'Ошибка',
                    description: response.data.message || 'Не удалось оформить подписку',
                });
            }
        } catch (error) {
            notification.error({
                message: 'Ошибка',
                description: error || 'Не удалось оформить подписку',
            });
        }
        setSubscribing(null);
    };

    if (loading) {
        return (
            <div style={{display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh'}}>
                <Spin size="large"/>
            </div>
        );
    }

    return (
        <Layout
            style={{
                marginTop: "50px",
                padding: "25px 0",
                background: "#fff",
                borderRadius: "11px",
                boxShadow: "0px 4px 10px rgba(130, 137, 147, 0.2)"
            }}
        >
            <Layout.Content style={{padding: "0 24px", minHeight: 350}}>
                <Title level={2} style={{textAlign: 'center', marginBottom: 32}}>
                    Выбор тарифа
                </Title>
                <Paragraph style={{textAlign: 'center', fontSize: 16, marginBottom: 40, color: '#666'}}>
                    Для продолжения работы необходимо оформить подписку. Выберите подходящий тариф.
                </Paragraph>

                {legalEntities.length === 0 ? (
                    <div style={{textAlign: 'center', padding: 40}}>
                        <Text type="secondary">
                            Для оформления подписки необходимо создать юридическое лицо
                        </Text>
                    </div>
                ) : (
                    <Row gutter={[0, 24]} justify="center" style={{marginBottom: 8}}>
                        <Col xs={24} sm={16} md={12} lg={8}>
                            <Text strong style={{display: 'block', marginBottom: 8}}>
                                Юридическое лицо для выставления счетов
                            </Text>
                            <Select
                                value={selectedLegalEntityId}
                                onChange={setSelectedLegalEntityId}
                                style={{width: '100%'}}
                                size="large"
                            >
                                {legalEntities.map((entity) => (
                                    <Select.Option key={entity.Id} value={entity.Id}>
                                        {entity.Name}
                                    </Select.Option>
                                ))}
                            </Select>
                        </Col>
                    </Row>
                )}

                {tariffs.length === 0 ? (
                    <div style={{textAlign: 'center', padding: 40}}>
                        <Text type="secondary">Нет доступных тарифов</Text>
                    </div>
                ) : (
                    <Row
                        gutter={[24, 24]}
                        justify="center"
                        style={{display: 'flex', alignItems: 'stretch'}}
                    >
                        {tariffs.map((tariff) => (
                            <Col
                                key={tariff.id}
                                xs={24}
                                sm={12}
                                md={8}
                                lg={6}
                                style={{display: 'flex'}}
                            >
                                <Card
                                    hoverable
                                    style={{
                                        borderRadius: 12,
                                        textAlign: 'center',
                                        width: '100%',
                                        height: '100%',
                                        display: 'flex',
                                        flexDirection: 'column',
                                    }}
                                    bodyStyle={{
                                        flex: 1,
                                        display: 'flex',
                                        flexDirection: 'column',
                                        padding: '24px 24px 0',
                                    }}
                                    actions={[
                                        <Button
                                            type="primary"
                                            size="large"
                                            loading={subscribing === tariff.id}
                                            onClick={() => handleSubscribe(tariff.id)}
                                            style={{borderRadius: 8, margin: '0 16px 16px'}}
                                        >
                                            {subscribing === tariff.id ? 'Оформление...' : 'Выбрать'}
                                        </Button>,
                                    ]}
                                >
                                    <div style={{flex: 1, display: 'flex', flexDirection: 'column'}}>
                                        <Title level={4} style={{margin: 0}}>
                                            {tariff.name}
                                        </Title>
                                        <div style={{
                                            fontSize: 32,
                                            fontWeight: 'bold',
                                            color: '#1890ff',
                                            margin: '16px 0',
                                        }}>
                                            {tariff.price.toLocaleString('ru-RU')} ₽
                                        </div>
                                        <div style={{flex: 1, display: 'flex', alignItems: 'top', justifyContent: 'center'}}>
                                            <Paragraph
                                                style={{
                                                    color: '#666',
                                                    fontSize: 14,
                                                    margin: 0,
                                                }}
                                            >
                                                {tariff.description}
                                            </Paragraph>
                                        </div>
                                    </div>
                                </Card>
                            </Col>
                        ))}
                    </Row>
                )}
            </Layout.Content>
        </Layout>
    );
}
